/**
 * <CopilotUsageCard /> — Settings dashboard widget showing today's AI spend.
 *
 * Renders:
 *   - "$X.XX of $Y.YY today" heading + progress bar (Copilot-only spend
 *     against COPILOT_DAILY_BUDGET_USD)
 *   - Per-action_type breakdown (chat / recap / quick_capture / send_update)
 *   - Empty-state message when Supabase isn't configured
 *
 * Refreshes via React Query every 60s while mounted; on first render the
 * stale-while-revalidate behaviour shows the previous values immediately.
 *
 * Failure path: when /api/copilot/usage errors, render a small inline
 * error pill but never crash the Settings page.
 */

import { useQuery } from '@tanstack/react-query';
import { Sparkles } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { copilotApi, type CopilotUsageResponse } from '@/lib/api';
import { cn } from '@/lib/utils';

const ACTION_TYPE_LABELS: Record<string, string> = {
  chat: 'Alice chat',
  recap: 'Drawer recap',
  triage: 'Quick Capture (triage)',
  summarize: 'Queue summaries',
  quick_capture: 'Quick Capture AI',
  send_update: 'Send Update AI',
};

export function CopilotUsageCard() {
  const { data, error, isLoading } = useQuery<CopilotUsageResponse, Error>({
    queryKey: ['copilot-usage'],
    queryFn: () => copilotApi.usage(),
    refetchInterval: 60_000,
    staleTime: 30_000,
  });

  return (
    <Card className="p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-2.5">
          <div className="mt-0.5 rounded-md bg-muted p-1.5">
            <Sparkles className="h-4 w-4 text-muted-foreground" aria-hidden />
          </div>
          <div>
            <h2 className="text-base font-semibold">Alice usage today</h2>
            <p className="mt-0.5 text-sm text-muted-foreground">
              Spend across all AI surfaces. Resets at UTC midnight.
            </p>
          </div>
        </div>
        {data?.today && (
          <UsageBadge
            spentCents={data.today.copilotOnlyCents}
            budgetCents={data.budget.dailyCapCents}
          />
        )}
      </div>

      {error && (
        <div className="mt-4 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-[13px] text-destructive">
          Couldn't load usage: {error.message}
        </div>
      )}

      {isLoading && !data && (
        <div className="mt-4 h-[60px] animate-pulse rounded-md bg-muted/50" />
      )}

      {data && (
        <>
          <BudgetBar
            spentCents={data.today.copilotOnlyCents}
            budgetCents={data.budget.dailyCapCents}
            className="mt-4"
          />

          <div className="mt-4 grid gap-2 sm:grid-cols-2">
            {Object.entries(data.today.byActionType)
              .filter(([, cents]) => cents > 0)
              .sort(([, a], [, b]) => b - a)
              .map(([type, cents]) => (
                <div
                  key={type}
                  className="flex items-center justify-between rounded-md border border-border bg-background px-3 py-2"
                >
                  <span className="text-[13px] text-foreground">
                    {ACTION_TYPE_LABELS[type] ?? type}
                  </span>
                  <span className="font-mono text-[12px] tabular-nums text-muted-foreground">
                    ${(cents / 100).toFixed(2)}
                  </span>
                </div>
              ))}
            {Object.values(data.today.byActionType).every((c) => c === 0) && (
              <div className="col-span-full rounded-md border border-dashed border-border bg-muted/30 px-3 py-3 text-[13px] text-muted-foreground">
                {data.note ?? 'No AI calls logged today.'}
              </div>
            )}
          </div>
        </>
      )}
    </Card>
  );
}

function UsageBadge({
  spentCents,
  budgetCents,
}: {
  spentCents: number;
  budgetCents: number | null;
}) {
  if (budgetCents === null) {
    return <Badge variant="muted">${(spentCents / 100).toFixed(2)} today</Badge>;
  }
  const ratio = budgetCents > 0 ? spentCents / budgetCents : 0;
  const variant = ratio >= 1 ? 'destructive' : ratio >= 0.75 ? 'warning' : 'muted';
  return (
    <Badge variant={variant as 'destructive' | 'warning' | 'muted'}>
      ${(spentCents / 100).toFixed(2)} of ${(budgetCents / 100).toFixed(2)}
    </Badge>
  );
}

function BudgetBar({
  spentCents,
  budgetCents,
  className,
}: {
  spentCents: number;
  budgetCents: number | null;
  className?: string;
}) {
  if (budgetCents === null || budgetCents === 0) return null;
  const ratio = Math.min(1, spentCents / budgetCents);
  const colorClass =
    ratio >= 1 ? 'bg-destructive' : ratio >= 0.75 ? 'bg-amber-500' : 'bg-primary';
  return (
    <div className={cn('h-1.5 w-full overflow-hidden rounded-full bg-muted', className)}>
      <div
        className={cn('h-full transition-all', colorClass)}
        style={{ width: `${Math.round(ratio * 100)}%` }}
        aria-label={`Copilot usage: ${Math.round(ratio * 100)}% of daily budget`}
      />
    </div>
  );
}
